import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Heart, Send, Loader2, CheckCircle, User, MessageSquare, Mic } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Dedications({ wsRef }) {
  const { user } = useAuth();
  const [fromName, setFromName] = useState('');
  const [toName, setToName] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle, sending, success, error
  const [currentDedication, setCurrentDedication] = useState(null);
  const [pendingDedications, setPendingDedications] = useState([]);
  
  const isDJ = user && ['dj', 'admin', 'mod'].includes(user.role);

  // Listen for dedication broadcasts
  useEffect(() => {
    if (!wsRef?.current) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'dedication') {
          setCurrentDedication(data.data);
          // Clear after 30 seconds
          setTimeout(() => setCurrentDedication(null), 30000);
        }
      } catch (e) {}
    };

    const ws = wsRef.current;
    ws.addEventListener('message', handleMessage);
    
    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [wsRef]);

  // Fetch pending dedications for DJs
  const fetchPending = useCallback(async () => {
    if (!isDJ) return;
    try {
      const response = await fetch(`${API_URL}/api/dedications/pending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingDedications(data);
      }
    } catch (err) {}
  }, [isDJ]);

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromName.trim() || !toName.trim() || !message.trim()) return;

    setStatus('sending');
    try {
      const response = await fetch(`${API_URL}/api/dedications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          from_name: fromName.trim(),
          to_name: toName.trim(),
          message: message.trim()
        })
      });

      if (response.ok) {
        setStatus('success');
        setFromName('');
        setToName('');
        setMessage('');
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (err) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const handleReadDedication = async (dedicationId) => {
    try {
      await fetch(`${API_URL}/api/dedications/${dedicationId}/read`, {
        method: 'PATCH',
        credentials: 'include'
      });
      fetchPending();
    } catch (err) {}
  };

  return (
    <div className="space-y-4">
      {/* Current Dedication Display (shown to all) */}
      {currentDedication && (
        <div 
          className="p-4 rounded-2xl animate-pulse"
          style={{ 
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(168, 85, 247, 0.2))',
            border: '2px solid rgba(236, 72, 153, 0.5)'
          }}
          data-testid="current-dedication"
        >
          <div className="flex items-start gap-3">
            <Heart className="w-6 h-6 flex-shrink-0" style={{ color: '#EC4899' }} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-main)' }}>
                "{currentDedication.message}"
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                From <span style={{ color: 'var(--primary)' }}>{currentDedication.from_name}</span> to{' '}
                <span style={{ color: '#EC4899' }}>{currentDedication.to_name}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* DJ Queue (shown to DJs only) */}
      {isDJ && pendingDedications.length > 0 && (
        <div 
          className="glass rounded-2xl p-4"
          data-testid="dj-dedications-queue"
        >
          <div className="flex items-center gap-2 mb-3">
            <Mic className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            <h4 className="text-sm font-bold" style={{ color: 'var(--text-main)' }}>
              Pending Dedications ({pendingDedications.length})
            </h4>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {pendingDedications.map((ded) => (
              <div 
                key={ded.dedication_id}
                className="flex items-start gap-2 p-2 rounded-lg"
                style={{ backgroundColor: 'var(--surface-highlight)' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate" style={{ color: 'var(--text-main)' }}>
                    "{ded.message}"
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {ded.from_name} → {ded.to_name}
                  </p>
                </div>
                <button
                  onClick={() => handleReadDedication(ded.dedication_id)}
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: '#EC4899', color: 'white' }}
                >
                  Read
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit Dedication Form */}
      <div 
        className="glass rounded-2xl p-4"
        data-testid="dedication-form"
      >
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5" style={{ color: '#EC4899' }} />
          <h3 
            className="text-lg font-bold"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Send a Dedication
          </h3>
        </div>

        {status === 'success' ? (
          <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
            <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
            <p className="text-sm" style={{ color: '#22C55E' }}>
              Dedication submitted! The DJ will read it on-air.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Your name"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  className="w-full p-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                  data-testid="dedication-from"
                />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Dedicate to..."
                  value={toName}
                  onChange={(e) => setToName(e.target.value)}
                  className="w-full p-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                  data-testid="dedication-to"
                />
              </div>
            </div>
            <textarea
              placeholder="Your message (e.g., 'Happy Birthday!', 'Love you always!')"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="w-full p-2 rounded-lg text-sm outline-none resize-none"
              style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
              data-testid="dedication-message"
            />
            <button
              type="submit"
              disabled={status === 'sending' || !fromName.trim() || !toName.trim() || !message.trim()}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl font-medium transition-all hover:scale-105 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #EC4899, #A855F7)', color: 'white' }}
              data-testid="dedication-submit"
            >
              {status === 'sending' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Heart className="w-4 h-4" />
              )}
              <span>{status === 'sending' ? 'Sending...' : 'Send Dedication'}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
